import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CartDrawer from '../home/components/CartDrawer';
import { useSEO } from '../../hooks/useSEO';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

interface ShopifyPolicy {
  title: string;
  body: string;
  handle: string;
  url: string;
}

export default function PrivacyPage() {
  const [policy, setPolicy] = useState<ShopifyPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://motionboyz.org';

  useSEO({
    title: 'Privacy Policy | Motion Boyz – How We Protect Your Data',
    description:
      'Read the Motion Boyz Privacy Policy. Understand how we collect, use, and protect your personal information when you shop with us.',
    canonical: '/privacy',
    ogImage: `${siteUrl}/og-image.jpg`,
    ogType: 'article',
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${SUPABASE_URL}/functions/v1/shopify-privacy-policy`);
        const data = await res.json();
        if (data.error) {
          setError(data.error);
        } else {
          setPolicy(data.policy);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load privacy policy.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchPolicy();
  }, []);

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <CartDrawer />

      {/* Hero Section */}
      <div className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/58edf040d576283f95ca6ae7d9df1926.jpg)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        <div className="relative z-10 text-center px-6 max-w-4xl w-full">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 mb-6">
            <i className="ri-shield-check-line text-[#00d4ff] text-sm" />
            <span className="text-[#00d4ff] text-xs tracking-widest uppercase font-semibold">Legal</span>
          </div>
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ textShadow: '0 0 30px rgba(0,212,255,0.3)' }}
          >
            Privacy Policy
          </h1>
          <p className="text-gray-400 text-base tracking-wide max-w-2xl mx-auto">
            How we collect, use, and protect your personal information
          </p>
          <p className="text-[#00d4ff] text-sm mt-4 tracking-wider">
            Sourced directly from our Shopify store
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-[90%] max-w-[1400px] mx-auto py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* Sidebar */}
          <aside className="hidden lg:block lg:w-64 flex-shrink-0">
            <div className="sticky top-24">
              <div className="p-5 bg-[#111] border border-[#00d4ff]/10 rounded-xl space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff]">
                    <i className="ri-information-line text-xs" />
                  </div>
                  <p className="text-gray-500 text-xs tracking-widest uppercase font-semibold">About This Policy</p>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2 text-gray-400">
                    <i className="ri-building-line text-[#00d4ff] mt-0.5 flex-shrink-0" />
                    <span><span className="text-white font-semibold">Business:</span> Motion Boyz</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-400">
                    <i className="ri-global-line text-[#00d4ff] mt-0.5 flex-shrink-0" />
                    <span><span className="text-white font-semibold">Website:</span> motionboyz.org</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-400">
                    <i className="ri-mail-line text-[#00d4ff] mt-0.5 flex-shrink-0" />
                    <a href="mailto:llc.motionboyz@gmail.com" className="text-[#00d4ff] hover:underline cursor-pointer text-xs">
                      llc.motionboyz@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-5 bg-[#111] border border-[#00d4ff]/10 rounded-xl">
                <p className="text-gray-500 text-xs tracking-widest uppercase font-semibold mb-3">Related Policies</p>
                <div className="space-y-1">
                  {[
                    { label: 'Return & Refund', path: '/refund', icon: 'ri-arrow-go-back-line' },
                    { label: 'Shipping Policy', path: '/shipping', icon: 'ri-truck-line' },
                    { label: 'Terms of Service', path: '/terms', icon: 'ri-file-text-line' },
                  ].map((link) => (
                    <button
                      key={link.path}
                      onClick={() => window.REACT_APP_NAVIGATE(link.path)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all duration-300 cursor-pointer flex items-center gap-2"
                    >
                      <i className={`${link.icon} text-[#00d4ff] text-xs`} />
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 max-w-4xl">

            {/* Loading State */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-24 gap-4">
                <div className="w-10 h-10 border-2 border-[#00d4ff] border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-400 text-sm tracking-wide">Loading privacy policy...</p>
              </div>
            )}

            {/* Error State */}
            {!loading && error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-10 text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-red-500/10">
                  <i className="ri-error-warning-line text-red-400 text-3xl" />
                </div>
                <p className="text-red-400 font-semibold mb-2">Unable to load policy</p>
                <p className="text-gray-400 text-sm">{error}</p>
              </div>
            )}

            {/* Policy Content */}
            {!loading && policy && (
              <>
                <div className="mb-12 p-6 rounded-2xl bg-[#00d4ff]/5 border border-[#00d4ff]/15">
                  <p className="text-gray-300 text-sm leading-relaxed">
                    At <strong className="text-white">Motion Boyz</strong>, your privacy matters. This policy explains how we collect, use, and protect your information.
                  </p>
                </div>

                <div
                  className="policy-content text-gray-400 leading-relaxed space-y-6
                    [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-white [&_h1]:mb-4 [&_h1]:mt-10 [&_h1]:tracking-tight
                    [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mb-4 [&_h2]:mt-8 [&_h2]:tracking-tight [&_h2]:flex [&_h2]:items-center [&_h2]:gap-3 [&_h2]:before:content-[''] [&_h2]:before:block [&_h2]:before:w-1 [&_h2]:before:h-7 [&_h2]:before:bg-[#00d4ff] [&_h2]:before:rounded-full
                    [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-white [&_h3]:mb-3 [&_h3]:mt-6
                    [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-gray-200 [&_h4]:mb-2 [&_h4]:mt-4
                    [&_p]:mb-4 [&_p]:text-gray-400
                    [&_ul]:list-none [&_ul]:ml-0 [&_ul]:mb-4 [&_ul]:space-y-2
                    [&_ul_li]:flex [&_ul_li]:items-start [&_ul_li]:gap-2 [&_ul_li]:text-gray-400 [&_ul_li]:before:content-['•'] [&_ul_li]:before:text-[#00d4ff] [&_ul_li]:before:mt-0.5
                    [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-4 [&_ol]:space-y-1
                    [&_ol_li]:text-gray-400
                    [&_a]:text-[#00d4ff] [&_a]:hover:underline [&_a]:cursor-pointer
                    [&_strong]:text-white [&_strong]:font-semibold
                    [&_hr]:border-white/10 [&_hr]:my-10"
                  dangerouslySetInnerHTML={{ __html: policy.body }}
                />
              </>
            )}

            {/* Bottom CTA */}
            {!loading && (
              <div className="mt-16 p-8 bg-gradient-to-br from-[#00d4ff]/5 to-transparent border border-[#00d4ff]/20 rounded-2xl text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#00d4ff]/10 text-[#00d4ff] mx-auto mb-4">
                  <i className="ri-shopping-bag-line text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Shop with Confidence</h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Your data is safe with us. Explore our exclusive collection of premium limited streetwear.
                </p>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/shop')}
                  className="px-8 py-3 bg-[#00d4ff] text-black font-bold text-sm tracking-widest uppercase rounded-full hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
                  style={{ boxShadow: '0 0 30px rgba(0,212,255,0.4)' }}
                >
                  Shop Now
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
